import { _isNil, _notNil } from '@/littledash';
import type { ChangeEventHandler, CSSProperties, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  DataTableCellIndexCoordinate,
  DataTableCellSelectionChangeEvent,
  DataTableCellSelectionState,
  DataTableCellState,
  DataTableDirection,
} from '@/components/UI/DataTableV2/DataTable.model';
import { DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import { DateInputUtils, DateUtils } from '@/utils/Date.utils';
import { useDataTable } from '../DataTableProvider';
import { useCellCoordinate } from './useCellCoordinate';
import { useCellData } from './useCellData';
import { useCellMouseHandlers } from './useCellMouseHandlers';
import { useCellPositioning } from './useCellPositioning';
import { useCellSelectionState } from './useCellSelectionState';
import { useCellState } from './useCellState';
import { useGlobalEventListenerAbort } from './useGlobalEventListenerAbort';

/**
 * Display format options for picker inputs
 *
 * Defines the precision and format of datetime values displayed in the picker.
 * Maps to HTML5 input types and determines conversion behavior.
 */
export type PickerDisplayFormat = 'date' | 'datetime-hm' | 'datetime-hms';

/**
 * Picker display format constants
 *
 * @example
 * // For timestamp cells with full precision
 * displayFormat: PickerDisplayFormat.DateTimeHoursMinutesSeconds
 *
 * @example
 * // For collect cells with minute precision
 * displayFormat: PickerDisplayFormat.DateTimeHoursMinutes
 */
export const PickerDisplayFormat = {
  /** Date only: 2024-02-15 */
  Date: 'date',
  /** DateTime with hours and minutes: 2024-02-15T10:30 */
  DateTimeHoursMinutes: 'datetime-hm',
  /** DateTime with hours, minutes, and seconds: 2024-02-15T10:30:45 */
  DateTimeHoursMinutesSeconds: 'datetime-hms',
} as const satisfies Record<string, PickerDisplayFormat>;

/**
 * Configuration for picker cell behavior
 *
 * @property coordinate - Cell position in the data table (index-based)
 * @property positionOffset - Pixel offset for positioning the picker input overlay
 * @property displayFormat - Controls picker precision (HH:MM vs HH:MM:SS)
 */
interface UsePickerCellBehaviorConfig {
  readonly coordinate: DataTableCellIndexCoordinate;
  readonly positionOffset: Readonly<{ x: number; y: number }>;
  readonly displayFormat: PickerDisplayFormat;
}

/**
 * Cell data and state information
 */
export interface PickerCellState {
  readonly value: string | null | undefined;
  readonly state: DataTableCellState;
  readonly selectionState: DataTableCellSelectionState;
  readonly hasGroupBorder: boolean;
  readonly tooltip: string | undefined;
}

/**
 * Picker control and state
 */
export interface PickerControl {
  readonly active: boolean;
  readonly inputRef: RefObject<HTMLInputElement | null>;
  readonly positionStyle: CSSProperties;
  readonly handleInputChange: ChangeEventHandler<HTMLInputElement>;
  readonly activate: () => void;
}

/**
 * Mouse interaction handlers for cell selection
 */
export interface PickerMouseHandlers {
  readonly onMouseDown: React.MouseEventHandler<HTMLDivElement>;
  readonly onMouseEnter: React.MouseEventHandler<HTMLDivElement>;
  readonly onMouseUp: React.MouseEventHandler<HTMLDivElement>;
}

/**
 * Return value from usePickerCellBehavior hook
 *
 * Provides all state, handlers, and refs needed to render a picker-enabled cell.
 * Properties are grouped into logical categories for better organization:
 * - `cell`: Cell data and visual state
 * - `picker`: Picker control and input handling
 * - `mouseHandlers`: Mouse interaction handlers for selection
 *
 * @example
 * const { cell, picker, mouseHandlers } = usePickerCellBehavior(config);
 *
 * return (
 *   <div
 *     onMouseDown={mouseHandlers.onMouseDown}
 *     style={picker.positionStyle}
 *   >
 *     {cell.value || 'Add Timestamp'}
 *     <input ref={picker.inputRef} onChange={picker.handleInputChange} />
 *   </div>
 * );
 */
export interface UsePickerCellBehaviorReturn {
  readonly cell: PickerCellState;
  readonly picker: PickerControl;
  readonly mouseHandlers: PickerMouseHandlers;
}

/**
 * Hook for date and datetime picker cell behavior
 *
 * Provides picker activation, value persistence, and keyboard shortcuts
 * for date or datetime entry in DataTable cells.
 *
 * Supports two modes based on `displayFormat`:
 * - **Date-only** (`PickerDisplayFormat.Date`): Uses `<input type="date">`, stores raw `YYYY-MM-DD`
 *   values with no timezone conversion. Validates with `DateUtils.isValidDate()`.
 * - **DateTime** (`DateTimeHoursMinutes` / `DateTimeHoursMinutesSeconds`): Uses `<input type="datetime-local">`,
 *   converts via `DateInputUtils` for ISO 8601 datetime with timezone handling.
 *
 * ## Features
 *
 * - **Picker activation**: Double-click or programmatic activation opens native date/datetime picker
 * - **Keyboard shortcuts**:
 *   - `Enter`: Save and navigate down (or up with Shift)
 *   - `Escape`: Cancel without saving
 * - **Workflow-aware navigation**: Respects workflow exit columns, preventing auto-navigation
 * - **Event cleanup**: Uses AbortController for proper listener cleanup
 *
 * ## Usage with Auto-Insert
 *
 * This hook provides picker control. For auto-insert behavior (Enter on empty cell),
 * use `useAutoInsertOnActivate` hook in combination with this one.
 *
 * @param config - Configuration object with coordinate and display format
 * @returns Object containing cell state, picker control, and mouse handlers
 *
 * @example
 * // Date-only cell (metadata date fields)
 * const { cell, picker, mouseHandlers } = usePickerCellBehavior({
 *   coordinate,
 *   positionOffset,
 *   displayFormat: PickerDisplayFormat.Date,
 * });
 *
 * @example
 * // Timestamp cell with seconds precision
 * const { cell, picker, mouseHandlers } = usePickerCellBehavior({
 *   coordinate,
 *   positionOffset,
 *   displayFormat: PickerDisplayFormat.DateTimeHoursMinutesSeconds,
 * });
 *
 * @example
 * // Collect cell with minute precision
 * const { cell, picker, mouseHandlers } = usePickerCellBehavior({
 *   coordinate,
 *   positionOffset,
 *   displayFormat: PickerDisplayFormat.DateTimeHoursMinutes,
 * });
 */
export const usePickerCellBehavior = (config: UsePickerCellBehaviorConfig): UsePickerCellBehaviorReturn => {
  const { coordinate, positionOffset, displayFormat } = config;
  const isDateOnly = displayFormat === PickerDisplayFormat.Date;
  const { dataTableService } = useDataTable();
  const pickerInputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(false);
  const [cellValueOverride, setCellValueOverride] = useState<string | null>(null);

  const handleAbort = useCallback(() => {
    setActive(false);
    setCellValueOverride(null);
  }, []);

  const { getSignal, abort } = useGlobalEventListenerAbort({ onAbort: handleAbort });

  const { idCoordinate, cellRef } = useCellCoordinate(coordinate);
  const { cellData } = useCellData(idCoordinate, cellRef);
  const { cellState, tooltip } = useCellState(idCoordinate, cellRef);
  const { selectionState } = useCellSelectionState(coordinate, idCoordinate);
  const { containerStyle } = useCellPositioning(positionOffset, active);

  const persistInputValue = useCallback(
    (moveAfterPersistDirection?: DataTableDirection) => {
      setActive(false);
      setCellValueOverride(null);
      abort('completed');

      if (_isNil(idCoordinate) || _isNil(pickerInputRef.current)) {
        return;
      }

      const persistValue = isDateOnly
        ? DateUtils.isValidDate(pickerInputRef.current.value)
          ? pickerInputRef.current.value
          : ''
        : DateInputUtils.localDateTimeToISODateTime(pickerInputRef.current.value);
      const shouldClearValue = persistValue === '' && _notNil(cellData?.value);
      const shouldUpdateValue = persistValue !== '' && persistValue !== cellData?.value;

      if (shouldClearValue) {
        dataTableService
          .updateCells([{ column_id: idCoordinate.column, row_id: idCoordinate.row, value: null }])
          .then(() => dataTableService.validate());
      } else if (shouldUpdateValue) {
        dataTableService
          .updateCells([{ column_id: idCoordinate.column, row_id: idCoordinate.row, value: persistValue }])
          .then(() => dataTableService.validate());
      }

      const shouldNavigate =
        _notNil(moveAfterPersistDirection) && !dataTableService.workflowService.workflowActionExit(idCoordinate.column);

      if (shouldNavigate) {
        dataTableService.moveSelection(moveAfterPersistDirection);
      }
    },
    [abort, cellData?.value, dataTableService, idCoordinate, isDateOnly]
  );

  const activatePicker = useCallback(() => {
    if (dataTableService.readonly || _isNil(pickerInputRef.current)) {
      return;
    }

    setActive(true);

    if (isDateOnly) {
      const dateValue =
        DateUtils.isValidDate(cellData?.value) && _notNil(cellData?.value) ? cellData.value : DateUtils.dateNow();
      pickerInputRef.current.value = dateValue;
    } else {
      const isoValue =
        DateUtils.isValidDateTime(cellData?.value) && _notNil(cellData?.value)
          ? cellData.value
          : DateUtils.dateTimeNow();
      const includeSeconds = displayFormat === PickerDisplayFormat.DateTimeHoursMinutesSeconds;
      pickerInputRef.current.value = DateInputUtils.toLocalDateTime(isoValue, { includeSeconds });
    }

    const signal = getSignal();
    const eventOptions = { once: true, capture: true, signal };

    // Listen for blur to persist when picker closes
    pickerInputRef.current.addEventListener('blur', () => persistInputValue(), eventOptions);

    // Listen for change events (fires when Clear button is clicked)
    pickerInputRef.current.addEventListener('change', () => persistInputValue(), eventOptions);

    pickerInputRef.current.addEventListener(
      'keyup',
      (event) => {
        if (event.code === 'Enter') {
          persistInputValue(event.shiftKey ? 'UP' : 'DOWN');
        } else if (event.code === 'Escape') {
          // Reset input to original value before persisting (cancel behavior)
          if (_notNil(pickerInputRef.current)) {
            if (isDateOnly) {
              const originalValue =
                DateUtils.isValidDate(cellData?.value) && _notNil(cellData?.value) ? cellData.value : '';
              pickerInputRef.current.value = originalValue;
            } else {
              const originalValue =
                DateUtils.isValidDateTime(cellData?.value) && _notNil(cellData?.value) ? cellData.value : '';
              const includeSeconds = displayFormat === PickerDisplayFormat.DateTimeHoursMinutesSeconds;
              pickerInputRef.current.value = originalValue
                ? DateInputUtils.toLocalDateTime(originalValue, { includeSeconds })
                : '';
            }
          }
          persistInputValue(); // Now persists the reset value (no-op)
        }
      },
      eventOptions
    );

    window.requestAnimationFrame(() => {
      pickerInputRef?.current?.showPicker();
      pickerInputRef?.current?.focus();
    });
  }, [cellData?.value, dataTableService.readonly, getSignal, persistInputValue, displayFormat, isDateOnly]);

  // Listen for Enter key activation on cells with existing values
  useEffect(() => {
    if (_notNil(idCoordinate) && _notNil(cellData?.value) && !dataTableService.readonly) {
      const abortController = new AbortController();

      const cellSelectionChangeListener = (event: DataTableCellSelectionChangeEvent) => {
        const fromCoordinate = event.detail.selection.from;
        const isThisCell = fromCoordinate.column === coordinate.column && fromCoordinate.row === coordinate.row;

        if (event.detail.activate && isThisCell) {
          activatePicker();
        }
      };

      dataTableService.subscribe(
        DataTableEvent.CellSelectionChange,
        cellSelectionChangeListener,
        abortController.signal
      );

      return () => {
        abortController.abort();
      };
    }
  }, [dataTableService, coordinate, idCoordinate, cellData?.value, activatePicker]);

  // Mouse interaction handlers
  const { handleMouseDown, handleMouseEnter, handleMouseUp } = useCellMouseHandlers(coordinate);

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const rawValue = event?.target?.value ?? '';
      const value = isDateOnly
        ? DateUtils.isValidDate(rawValue)
          ? rawValue
          : ''
        : DateInputUtils.localDateTimeToISODateTime(rawValue);
      setCellValueOverride(value === '' ? null : value);
    },
    [isDateOnly]
  );

  const cellValue = cellValueOverride ?? cellData?.value;
  const hasGroupBorder = dataTableService.rightBorderType(coordinate.column) === 'group-separator';

  return {
    cell: {
      value: cellValue,
      state: cellState,
      selectionState,
      hasGroupBorder,
      tooltip,
    },
    picker: {
      active,
      inputRef: pickerInputRef,
      positionStyle: containerStyle,
      handleInputChange,
      activate: activatePicker,
    },
    mouseHandlers: {
      onMouseDown: handleMouseDown,
      onMouseEnter: handleMouseEnter,
      onMouseUp: handleMouseUp,
    },
  };
};
